import ErrorNotice from "../../errors/ErrorNotice";
import type { UiError } from "../../errors/uiError";
import LoadingIndicator from "./LoadingIndicator";

/** The part of a load state that is not a value: what LoadStatus shows. */
type PendingLoad = { readonly status: "loading" } | { readonly status: "failed"; readonly error: UiError } | { readonly status: "loaded" };

interface LoadStatusProps {
  state: PendingLoad;
  /** What is loading, as the spinner's text. */
  loadingLabel: string;
  /** Offers to load again after a failure. */
  onRetry?: () => void;
  /** The short failure notice, for side panels and rows. */
  compact?: boolean;
  /** Spacing around the spinner, such as a taller pane. */
  loadingClassName?: string;
  /** Spacing around the failure notice. */
  failedClassName?: string;
  /** What failed, in the page's words; the error's own title otherwise. */
  failedTitle?: string;
}

/**
 * A pane's data while it is not there: a spinner while it loads, the failure with a retry
 * when it fails, nothing once it has loaded. Return it while `state.status !== "loaded"`,
 * or place it above the loaded content.
 */
export default function LoadStatus({ state, loadingLabel, onRetry, compact = false, loadingClassName, failedClassName, failedTitle }: LoadStatusProps) {
  switch (state.status) {
    case "loading":
      return <LoadingIndicator label={loadingLabel} className={loadingClassName} />;
    case "failed":
      return <ErrorNotice error={state.error} onRetry={onRetry} compact={compact} className={failedClassName} title={failedTitle} />;
    case "loaded":
      return null;
  }
}
